import { TrophyIcon } from '@heroicons/react/24/outline';
import { Suspense, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import useMe from '@/renderer/utils/useMe';

import type { ResultsFilters, ResultsFormFilters } from './FilterTypes';
import { MemberFuturesSection } from './MemberFuturesSection';
import { OddsCredit } from './OddsCredit';
import { PendingSection } from './PendingSection';
import { ResultsEmpty, SectionHead } from './PlayRow';
import { ResultsBreakdowns, ResultsBreakdownsContent } from './ResultsBreakdowns';
import { type MemberTab, ResultsControls } from './ResultsControls';
import { ResultsReconcileProvider } from './ResultsReconcileContext';
import ResultsTable, { GRADED_RESULTS, ResultsTableContent } from './ResultsTable';
import { resolveSportIds } from './consts';

export function ResultsContents({
  baseUrl,
  userId,
  filters,
  form,
  hasNoPlays = false,
  pendingCount = null,
  onPendingCount,
}: {
  baseUrl: string;
  form: UseFormReturn<ResultsFormFilters>;
  userId: API.Id | 'me' | 'archive';
  filters: ResultsFilters;
  breakdown?: string;
  title?: string;
  // Nothing graded and nothing pending in this view.
  hasNoPlays?: boolean;
  // Pending rows in view (null until known). The odds credit sits with the pending
  // section when it shows real rows (current lines), else with Results (closing lines).
  pendingCount?: number | null;
  onPendingCount?: (count: number) => void;
}) {
  const hideProps = !!form.watch('hidePropsFutures');
  const { sportId: sportIdBase, startDate, endDate, playType, gameType, free } = filters;

  const sportId = useMemo(() => resolveSportIds(sportIdBase), [sportIdBase]);

  const serviceIds = useMemo(() => (sportIdBase === 'plus' ? [46, 59] : undefined), [sportIdBase]);

  // The member's own view splits current action into two tabs: game "Plays" (the
  // default) and season-long "Futures", so pending futures have a home of their own
  // and never mix back into the game-pending list (#1581).
  const [memberTab, setMemberTab] = useState<MemberTab>('plays');
  const isMember = userId === 'me';
  const showFutures = isMember && memberTab === 'futures';

  // Staff/admin bypass the "subscribe to view" paywall: the server returns their real
  // pending picks, so the archive pending section shows real rows instead of the blurred
  // teaser (view-access only, #1531).
  const me = useMe();
  const isStaff = me?.role === 'staff' || me?.role === 'admin';

  // The archive teaser stands in for pending rows for non-staff, so it shows no lines.
  const pendingShowsLines = !!pendingCount && (userId !== 'archive' || isStaff);

  // Graded "Results" empty state — contextual to who's looking.
  const resultsEmpty = isMember ? (
    <ResultsEmpty
      Icon={TrophyIcon}
      title="No graded plays yet"
      description="Your play history will appear here once you receive your first pick."
      ctaLabel="View all results →"
      ctaHref="/archive"
    />
  ) : (
    <ResultsEmpty
      Icon={TrophyIcon}
      title="Nothing here yet"
      description="No plays match this view. Try another season or sport."
    />
  );

  return (
    // Scope both lists (pending + graded) so a grade in one reconciles the other live,
    // no page refresh needed (#1284).
    <ResultsReconcileProvider>
      <Suspense
        fallback={<ResultsBreakdownsContent baseUrl={baseUrl} userId={userId} filters={filters} />}
      >
        <ResultsBreakdowns baseUrl={baseUrl} userId={userId} filters={filters} />
      </Suspense>

      {/* Plays/Futures lives in the controls row as a segmented control rather than as
          a separate tab strip above it (#2004). */}
      {!hasNoPlays && (
        <ResultsControls
          hideProps={hideProps}
          onHideProps={(next) => form.setValue('hidePropsFutures', next)}
          memberTab={memberTab}
          onMemberTab={setMemberTab}
          showMemberTabs={isMember}
        />
      )}

      {/* Futures tab: the member's pending futures (received ones count; ghosts are
          shown but never counted). Graded futures stay under the graded results. */}
      {showFutures && (
        <MemberFuturesSection userId={userId} sportId={sportId} serviceIds={serviceIds} />
      )}

      {/* Plays tab (and every non-member view): Pending section + graded Results. */}
      {!showFutures && (
        <>
          <PendingSection
            userId={userId}
            sportId={sportId}
            serviceIds={serviceIds}
            sportIdBase={sportIdBase}
            isStaff={isStaff}
            isAdmin={me?.role === 'admin'}
            onCount={onPendingCount}
          />

          <section>
            <SectionHead
              label="Results"
              // Wait for the pending count, so the credit doesn't render here and then
              // jump up to Pending once the count arrives.
              aside={
                pendingCount !== null && !hasNoPlays && !pendingShowsLines ? (
                  <OddsCredit />
                ) : undefined
              }
            />
            <Suspense
              fallback={
                <ResultsTableContent
                  userId={userId}
                  infiniteRequest={null}
                  emptyState={resultsEmpty}
                />
              }
            >
              <ResultsTable
                sportId={sportId}
                serviceIds={serviceIds}
                playType={playType}
                gameType={gameType}
                startDate={startDate}
                endDate={endDate}
                userId={userId}
                free={free}
                // Both views show graded-only here — pending plays live in the section
                // above, so they never duplicate.
                result={GRADED_RESULTS}
                emptyState={resultsEmpty}
              />
            </Suspense>
            {/* A key for Win/Loss/Push is noise when there are no results to key. */}
            <div
              className={twMerge(
                'mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 px-0.5 text-xs text-zinc-400 dark:text-zinc-500',
                hasNoPlays && 'hidden',
              )}
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-emerald-500" />
                Win
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-rose-500" />
                Loss
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-zinc-400" />
                Push
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="rounded-[5px] bg-violet-100 px-1.5 py-0.5 font-bold text-[9px] text-violet-600 uppercase tracking-wide dark:bg-violet-400/15 dark:text-violet-300">
                  Free
                </span>
                not counted
              </span>
            </div>
          </section>
        </>
      )}
    </ResultsReconcileProvider>
  );
}
